import { useState, useEffect } from 'react';

// Parsed conflict entry from error message
export interface ConflictingEntry {
  calllogId: number;
  storeLabel: string;      // "PX #1751" or "Store #1234"
  timeRange: string;       // "10:00 AM - 12:00 PM"
  startTime: string;       // Parsed start time for calculations (HH:MM format)
  endTime: string;         // Parsed end time for calculations (HH:MM format)
  conflictType: string;    // "Drive time overlapping another call time", etc.
}

interface TimeConflictEditorProps {
  errorMessage: string;
  conflictingEntries: ConflictingEntry[];
  proposedTimeIn: string;   // HH:MM format
  proposedTimeOut: string;  // HH:MM format
  proposedDate: string;     // YYYY-MM-DD format
  onTimesChanged: (timeIn: string, timeOut: string) => void;
  onRetry: () => void;
  onCancel: () => void;
}

// Helper to format HH:MM to displayable time (e.g., "9:30 AM")
function formatTimeDisplay(time24: string): string {
  if (!time24) return '--:--';
  const [hourStr, minStr] = time24.split(':');
  const hour = parseInt(hourStr, 10);
  const min = minStr || '00';

  if (isNaN(hour)) return time24;

  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${hour12}:${min} ${period}`;
}

// Helper to parse time from display format (e.g., "9:30 AM") to HH:MM
function parseTimeInput(timeStr: string): string | null {
  // Try 12-hour format (9:30 AM)
  const match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    let hour = parseInt(match12[1], 10);
    const min = match12[2];
    const period = match12[3].toUpperCase();

    if (period === 'PM' && hour !== 12) hour += 12;
    if (period === 'AM' && hour === 12) hour = 0;

    return `${hour.toString().padStart(2, '0')}:${min}`;
  }

  // Try 24-hour format (14:00)
  const match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (match24) {
    const hour = parseInt(match24[1], 10);
    const min = match24[2];
    return `${hour.toString().padStart(2, '0')}:${min}`;
  }

  return null;
}

// Calculate duration in minutes between two HH:MM times
function calculateDuration(startTime: string, endTime: string): number {
  if (!startTime || !endTime) return 0;

  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  return Math.max(0, endMinutes - startMinutes);
}

// Format duration in minutes to display string
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

// Parse conflict info from error message
// Example: "Call time overlapping another call time (conflicts with entry #12345 at PX #1751: 10:00 AM - 12:00 PM)"
export function parseConflictFromError(errorMessage: string): ConflictingEntry | null {
  // Match the conflict pattern
  const match = errorMessage.match(
    /(.+?)\s*\(conflicts with entry #(\d+) at (.+?):\s*(\d{1,2}:\d{2}\s*(?:AM|PM)?)\s*-\s*(\d{1,2}:\d{2}\s*(?:AM|PM)?)\)/i
  );

  if (!match) return null;

  const conflictType = match[1].trim();
  const calllogId = parseInt(match[2], 10);
  const storeLabel = match[3].trim();
  const startTimeDisplay = match[4].trim();
  const endTimeDisplay = match[5].trim();

  // Parse display times to HH:MM
  const startTime = parseTimeInput(startTimeDisplay) || startTimeDisplay;
  const endTime = parseTimeInput(endTimeDisplay) || endTimeDisplay;

  return {
    calllogId,
    storeLabel,
    timeRange: `${startTimeDisplay} - ${endTimeDisplay}`,
    startTime,
    endTime,
    conflictType,
  };
}

export default function TimeConflictEditor({
  conflictingEntries,
  proposedTimeIn,
  proposedTimeOut,
  onTimesChanged,
  onRetry,
  onCancel,
}: TimeConflictEditorProps) {
  // Local state for editing
  const [editingTimeIn, setEditingTimeIn] = useState(false);
  const [editingTimeOut, setEditingTimeOut] = useState(false);
  const [tempTimeValue, setTempTimeValue] = useState('');
  const [timeIn, setTimeIn] = useState(proposedTimeIn);
  const [timeOut, setTimeOut] = useState(proposedTimeOut);

  // Update local state when props change
  useEffect(() => {
    setTimeIn(proposedTimeIn);
    setTimeOut(proposedTimeOut);
  }, [proposedTimeIn, proposedTimeOut]);

  // Notify parent of changes
  useEffect(() => {
    if (timeIn !== proposedTimeIn || timeOut !== proposedTimeOut) {
      onTimesChanged(timeIn, timeOut);
    }
  }, [timeIn, timeOut, proposedTimeIn, proposedTimeOut, onTimesChanged]);

  const handleTimeInEdit = () => {
    setTempTimeValue(formatTimeDisplay(timeIn));
    setEditingTimeIn(true);
  };

  const handleTimeOutEdit = () => {
    setTempTimeValue(formatTimeDisplay(timeOut));
    setEditingTimeOut(true);
  };

  const handleTimeConfirm = (type: 'in' | 'out') => {
    const parsed = parseTimeInput(tempTimeValue);
    if (parsed) {
      if (type === 'in') {
        setTimeIn(parsed);
        setEditingTimeIn(false);
      } else {
        setTimeOut(parsed);
        setEditingTimeOut(false);
      }
    }
    setTempTimeValue('');
  };

  const handleTimeCancel = () => {
    setEditingTimeIn(false);
    setEditingTimeOut(false);
    setTempTimeValue('');
  };

  // Quick fix suggestions - find time slots that don't conflict
  const generateQuickFixes = (): { label: string; timeIn: string; timeOut: string }[] => {
    const fixes: { label: string; timeIn: string; timeOut: string }[] = [];
    const duration = calculateDuration(proposedTimeIn, proposedTimeOut);

    for (const conflict of conflictingEntries) {
      // Suggestion 1: Start after conflicting entry ends
      if (conflict.endTime) {
        fixes.push({
          label: `Start after: ${formatTimeDisplay(conflict.endTime)}`,
          timeIn: conflict.endTime,
          timeOut: addMinutesToTime(conflict.endTime, duration),
        });
      }

      // Suggestion 2: End before conflicting entry starts
      if (conflict.startTime) {
        const suggestedTimeOut = conflict.startTime;
        const suggestedTimeIn = subtractMinutesFromTime(suggestedTimeOut, duration);
        if (suggestedTimeIn) {
          fixes.push({
            label: `End before: ${formatTimeDisplay(conflict.startTime)}`,
            timeIn: suggestedTimeIn,
            timeOut: suggestedTimeOut,
          });
        }
      }
    }

    return fixes;
  };

  // Helper to add minutes to a time
  function addMinutesToTime(time: string, minutes: number): string {
    const [hour, min] = time.split(':').map(Number);
    const totalMinutes = hour * 60 + min + minutes;
    const newHour = Math.floor(totalMinutes / 60) % 24;
    const newMin = totalMinutes % 60;
    return `${newHour.toString().padStart(2, '0')}:${newMin.toString().padStart(2, '0')}`;
  }

  // Helper to subtract minutes from a time
  function subtractMinutesFromTime(time: string, minutes: number): string | null {
    const [hour, min] = time.split(':').map(Number);
    const totalMinutes = hour * 60 + min - minutes;
    if (totalMinutes < 0) return null;
    const newHour = Math.floor(totalMinutes / 60);
    const newMin = totalMinutes % 60;
    return `${newHour.toString().padStart(2, '0')}:${newMin.toString().padStart(2, '0')}`;
  }

  const applyQuickFix = (fix: { timeIn: string; timeOut: string }) => {
    setTimeIn(fix.timeIn);
    setTimeOut(fix.timeOut);
  };

  const quickFixes = generateQuickFixes();
  const duration = calculateDuration(timeIn, timeOut);

  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
      {/* Header */}
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-200">
          <svg className="h-5 w-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div>
          <h3 className="font-semibold text-amber-800">Time Conflict Detected</h3>
          <p className="text-sm text-amber-700">
            Your entry conflicts with existing visit(s). Adjust your times below.
          </p>
        </div>
      </div>

      {/* Conflicting Entries */}
      <div className="mb-4 space-y-2">
        <div className="text-xs font-medium uppercase tracking-wide text-amber-700">
          Existing Entries
        </div>
        {conflictingEntries.map((entry, idx) => (
          <div
            key={`conflict-${entry.calllogId}-${idx}`}
            className="rounded-lg bg-white p-3 border border-amber-200"
          >
            <div className="flex items-center gap-2">
              <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <span className="font-medium text-slate-700">{entry.storeLabel}</span>
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-slate-600">
              <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{entry.timeRange}</span>
            </div>
            <div className="mt-1 text-xs text-amber-600">
              {entry.conflictType}
            </div>
          </div>
        ))}
      </div>

      {/* Your Proposed Times (Editable) */}
      <div className="mb-4 rounded-lg bg-white p-3 border border-slate-200">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
          Your Proposed Times
        </div>

        {/* Time In */}
        <div className="flex items-center justify-between py-1.5">
          <span className="text-sm text-slate-600">Time In</span>
          {editingTimeIn ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tempTimeValue}
                onChange={(e) => setTempTimeValue(e.target.value)}
                placeholder="9:30 AM"
                className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                autoFocus
              />
              <button onClick={() => handleTimeConfirm('in')} className="text-green-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button onClick={handleTimeCancel} className="text-red-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={handleTimeInEdit}
              className="flex items-center gap-1 text-sm font-medium text-primary-600"
            >
              {formatTimeDisplay(timeIn)}
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </div>

        {/* Time Out */}
        <div className="flex items-center justify-between py-1.5">
          <span className="text-sm text-slate-600">Time Out</span>
          {editingTimeOut ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={tempTimeValue}
                onChange={(e) => setTempTimeValue(e.target.value)}
                placeholder="1:00 PM"
                className="w-24 rounded border border-slate-300 px-2 py-1 text-sm"
                autoFocus
              />
              <button onClick={() => handleTimeConfirm('out')} className="text-green-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </button>
              <button onClick={handleTimeCancel} className="text-red-600">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={handleTimeOutEdit}
              className="flex items-center gap-1 text-sm font-medium text-primary-600"
            >
              {formatTimeDisplay(timeOut)}
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
          )}
        </div>

        {/* Duration */}
        <div className="flex items-center justify-between border-t border-slate-200 pt-2 mt-2">
          <span className="text-sm font-medium text-slate-700">Duration</span>
          <span className="text-sm font-semibold text-slate-800">
            {formatDuration(duration)}
          </span>
        </div>
      </div>

      {/* Quick Fix Suggestions */}
      {quickFixes.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            Quick Fixes
          </div>
          <div className="flex flex-wrap gap-2">
            {quickFixes.slice(0, 2).map((fix, idx) => (
              <button
                key={`fix-${idx}`}
                onClick={() => applyQuickFix(fix)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
              >
                {fix.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={onCancel}
          className="flex-1 rounded-lg border border-slate-200 bg-white py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          onClick={onRetry}
          className="flex-1 rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
        >
          Try Again
        </button>
      </div>
    </div>
  );
}
